/**
 * Single source of truth for parsing the PadSplit earnings breakdown from a
 * `padsplit_earnings` income transaction's `notes` field.
 *
 * This module is imported by BOTH the server analytics engine
 * (`server/services/analytics-engine.ts`) and the client P&L logic
 * (`client/src/lib/pnl-profit.ts`) so that analytics revenue and P&L numbers
 * can never silently diverge when PadSplit changes its earnings-note format.
 * When the notes format changes, update the parser here once.
 */

export type PadsplitBreakdown = {
  dues: number;
  moveInFees: number;
  commitmentFees: number;
  lateFees: number;
  otherBills: number;
  bookingFees: number;
  serviceFees: number;
};

/**
 * Parse the PadSplit earnings breakdown from a padsplit_earnings income
 * transaction's `notes` field. Returns null when no recognised fields are
 * found (pre-breakdown history or non-PS rows).
 */
export function parsePadsplitBreakdown(
  notes: string | null | undefined,
): PadsplitBreakdown | null {
  if (!notes) return null;
  // PadSplit emits both "$-2.73" (seen on negative collections) and "-$2.73"
  // (fees). Capture the whole token so collection signs can be retained.
  const dMatch = notes.match(/Dues:\s*(-?\$-?[\d.]+)/i);
  const miMatch = notes.match(/Move-in(?:\s*fees)?:\s*(-?\$-?[\d.]+)/i);
  const cmMatch = notes.match(/Commitment(?:\s*fees)?:\s*(-?\$-?[\d.]+)/i);
  const lfMatch = notes.match(/Late fees:\s*(-?\$-?[\d.]+)/i);
  const obMatch = notes.match(/Other(?:\s*bills)?:\s*(-?\$-?[\d.]+)/i);
  const bfMatch = notes.match(/Booking fees:\s*(-?\$-?[\d.]+)/i);
  const sfMatch = notes.match(/Service fees:\s*(-?\$-?[\d.]+)/i);
  if (!dMatch && !miMatch && !cmMatch && !lfMatch && !obMatch && !bfMatch && !sfMatch) return null;
  const signedAmount = (match: RegExpMatchArray | null) => {
    if (!match) return 0;
    const amount = Number(match[1].replace("$", ""));
    return Number.isFinite(amount) ? amount : 0;
  };
  return {
    dues: signedAmount(dMatch),
    moveInFees: signedAmount(miMatch),
    commitmentFees: signedAmount(cmMatch),
    lateFees: signedAmount(lfMatch),
    otherBills: signedAmount(obMatch),
    bookingFees: Math.abs(signedAmount(bfMatch)),
    serviceFees: Math.abs(signedAmount(sfMatch)),
  };
}
